import log from '@/lib/logger';
import {Controller, useForm} from 'react-hook-form';
import {z} from 'zod';
import {zodResolver} from '@hookform/resolvers/zod';
import {useMembersTranslation, useCommonTranslation} from '@/i18n/hooks';
import {Dialog, DialogActions, DialogBody, DialogTitle} from '@/components/tailwind/dialog';
import {Button} from '@/components/tailwind/button';
import {Input} from '@/components/tailwind/input';
import {Member, MemberDeleteBankingDetailsRequest, MemberUpdateBankingDetailsRequest} from "@/models/member.ts";
import ValidationAlert from "@/components/validation-alert.tsx";
import {Field, Label} from "@/components/tailwind/fieldset.tsx";
import {useAuth} from "@/context/use-auth.tsx";
import {useEffect, useMemo, useState} from "react";
import {isDefined} from "@/helpers/sanitizer.ts";
import {isValidIBAN} from "ibantools-germany";
import {isValidBIC} from "bankdata-germany";
import {deleteBankingDetails, updateBankingDetails} from "@/services/member-services.ts";
import {useToast} from "@/hooks/use-toast.ts";
import {ConfirmationDialog, useConfirmationDialog} from '@/components/confirmation-dialog';
import {v4 as uuid} from "uuid";
import {formatIBAN, unformatIBAN} from "@/helpers/iban-formatter.ts";

interface MemberEditBankDetailsDialogProps {
    isOpen: boolean;
    onSave: (data: Member) => void;
    onClose: () => void;
    onDelete: () => void;
    memberData: Member;
}


export default function MemberEditBankingDetailsDialog({
                                                           isOpen,
                                                           onSave,
                                                           onClose,
                                                           onDelete,
                                                           memberData,
                                                       }: MemberEditBankDetailsDialogProps) {
    const {t} = useMembersTranslation();
    const {t: tCommon} = useCommonTranslation();
    const [isIbanEditable, setIsIbanEditable] = useState(false);
    const confirmationDialog = useConfirmationDialog();
    const {toast} = useToast();
    const isNewEntry = !isDefined(memberData.bankingDetails?.id)

    const memberEditBankDetailsSchema = z.object({
        accountOwner: z.string().optional(),
        mandateReference: z.string().optional(),
        iban: z.string().optional(),
        bic: z.string().optional(),
        bankName: z.string().optional(),
        mandateDate: z.string().optional(),
    }).superRefine((data, ctx) => {
        if (data.bic && data.bic.trim() !== '' && !isValidBIC(data.bic)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "memberEditDialog.bankingDetails.errors.invalid-bic",
                path: ["bic"]
            });
        }

        if (isNewEntry || isIbanEditable) {
            if (!data.iban || data.iban.trim() === '') {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: "memberEditDialog.bankingDetails.errors.invalid-iban",
                    path: ["iban"]
                });
            } else if (!isValidIBAN(data.iban.replace(/\s+/g, ''))) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: "memberEditDialog.bankingDetails.errors.invalid-iban",
                    path: ["iban"]
                });
            }
        }

        if (data.mandateDate && data.mandateDate.trim() !== '' && isNaN(Date.parse(data.mandateDate))) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "memberEditDialog.bankingDetails.errors.invalid-mandate-date-format",
                path: ["mandateDate"]
            });
        }

        if ((isNewEntry || (data.iban && data.iban.trim() !== '')) &&
            (!data.mandateDate || data.mandateDate.trim() === '')) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "memberEditDialog.bankingDetails.errors.mandate-date-required",
                path: ["mandateDate"]
            });
        }

        if ((isNewEntry || (data.iban && data.iban.trim() !== '')) &&
            (!data.mandateReference || data.mandateReference.trim() === '')) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "memberEditDialog.bankingDetails.errors.mandate-reference-required",
                path: ["mandateReference"]
            });
        }
    });

    type MemberEditBankDetailsFormData = z.infer<typeof memberEditBankDetailsSchema>;

    const defaultValues = useMemo(() => ({
        accountOwner: memberData.bankingDetails?.accountOwner || '',
        iban: memberData.bankingDetails?.iban || '',
        bic: memberData.bankingDetails?.bic || '',
        bankName: memberData.bankingDetails?.bankName || '',
        mandateReference: memberData.bankingDetails?.mandateReference ||
            String(memberData.memberNo) || '',
        mandateDate: memberData.bankingDetails?.mandateDate ||
            (isNewEntry ? new Date().toISOString().split('T')[0] : ''),
    }), [
        memberData.memberNo,
        memberData.bankingDetails?.accountOwner,
        memberData.bankingDetails?.iban,
        memberData.bankingDetails?.bic,
        memberData.bankingDetails?.mandateReference,
        memberData.bankingDetails?.mandateDate,
        memberData.bankingDetails?.bankName
    ]);

    const {
        register,
        handleSubmit,
        control,
        setValue,
        watch,
        setError,
        formState: {errors},
        reset
    } = useForm<MemberEditBankDetailsFormData>({
        resolver: zodResolver(memberEditBankDetailsSchema),
        defaultValues
    });

    const ibanValue = watch('iban');

    useEffect(() => {
        if (isOpen && memberData) {
            reset(defaultValues);
            setIsIbanEditable(!isDefined(memberData.bankingDetails?.iban));
        }
    }, [isOpen, memberData, reset, defaultValues]);

    const {token} = useAuth();

    function handleEditIban() {
        setIsIbanEditable(true);
        setValue('iban', '');
    }

    async function saveData(data: MemberEditBankDetailsFormData): Promise<boolean> {
        // Mandate reference and date MUST BE set here - otherwise
        const bankDetailsData: MemberUpdateBankingDetailsRequest = {
            id: memberData.bankingDetails?.id || uuid(),
            memberId: memberData.id,
            accountOwner: data.accountOwner ? data.accountOwner : undefined,
            bankName: data.bankName ? data.bankName : undefined,
            bic: data.bic ? data.bic : undefined,
            mandateDate: data.mandateDate!,
            mandateReference: data.mandateReference!,
            isNew: isNewEntry,
            ibanEdited: isIbanEditable,
            iban: isIbanEditable ? data.iban : undefined
        };

        try {
            const response = await updateBankingDetails(token, bankDetailsData);
            if (response) {
                onSave(response);
            }
            return true;
        } catch (error: any) {
            log.error("Error updating member bank details: ", error);
            if (error.status) {
                setError("root", {
                    message: t("common.serverError.response-code-with-details", {
                        statusCode: error.status,
                        errorDetails: error.error
                    })
                });
            } else {
                setError("root", {message: t("common.serverError.unknown-with-details", {errorDetails: error.message})});
            }
            return false;
        }
    }

    async function onSubmit(data: MemberEditBankDetailsFormData) {
        if (await saveData(data)) {
            handleClosing();
        }
    }

    async function deleteBankingDetailsData(): Promise<boolean> {
        const deleteDetailDTO: MemberDeleteBankingDetailsRequest = {
            memberId: memberData.id,
            bankAccountId: memberData.bankingDetails!.id,
        }

        try {
            await deleteBankingDetails(token, deleteDetailDTO);
            return true;
        } catch (error: any) {
            log.error("Error updating member bank details: ", error);
            if (error.status) {
                setError("root", {
                    message: t("common.serverError.response-code-with-details", {
                        statusCode: error.status,
                        errorDetails: error.error
                    })
                });
            } else {
                setError("root", {message: t("common.serverError.unknown-with-details", {errorDetails: error.message})});
            }
            return false;
        }

    }

    async function onDeleteDetails() {
        if (await deleteBankingDetailsData()) {
            toast({
                title: t('memberEditDialog.bankingDetails.deleteConfirmation.toast.title'),
                description: t('memberEditDialog.bankingDetails.deleteConfirmation.toast.description'),
                duration: 15000,
            });
            onDelete();
            handleClosing();
        }
    }

    const openDeleteConfirmation = () => {
        confirmationDialog.openWithConfig({
            title: "deleteConfirmation.bankingDetails.title",
            description: "deleteConfirmation.bankingDetails.description",
            cancelButton: {
                text: "deleteConfirmation.buttons.cancel.text",
                ariaLabel: "deleteConfirmation.buttons.cancel.ariaLabel"
            },
            confirmButton: {
                text: "deleteConfirmation.buttons.submit.text",
                ariaLabel: "deleteConfirmation.buttons.submit.ariaLabel",
                color: "red"
            }
        });
    }

    function handleClosing() {
        reset();
        setIsIbanEditable(false);
        onClose();
    }

    return (
        <Dialog open={isOpen} onClose={onClose}>
            <DialogTitle>
                {t('memberEditDialog.bankingDetails.title')}
            </DialogTitle>

            <DialogBody>
                {errors.root && (
                    <ValidationAlert message={errors.root?.message ||
                        t("common.serverError.unknown")}/>
                )}
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                    <div>
                        <Field>
                            <Label>{t('memberEditDialog.bankingDetails.fields.accountOwner.label')}</Label>
                            <Input
                                {...register("accountOwner")}
                                type="text"
                                autoComplete="name"
                                invalid={Boolean(errors.accountOwner)}
                                placeholder={t('memberEditDialog.bankingDetails.fields.accountOwner.placeholder')}
                                aria-label={t('memberEditDialog.bankingDetails.fields.accountOwner.ariaLabel')}
                            />
                        </Field>
                        {errors.accountOwner && (
                            <ValidationAlert
                                message={t(errors.accountOwner?.message ||
                                    "memberEditDialog.bankingDetails.errors.unknown-accountOwner-issue")}
                            />
                        )}
                    </div>

                    <div>
                        <Field>
                            <Label>
                                {t('memberEditDialog.bankingDetails.fields.iban.label')}
                            </Label>
                            <div className="flex items-center space-x-2">
                                <Controller
                                    name="iban"
                                    control={control}
                                    render={({ field }) => (
                                        <Input
                                            type="text"
                                            autoComplete="iban"
                                            disabled={!isIbanEditable}
                                            invalid={Boolean(errors.iban)}
                                            placeholder={isIbanEditable ?
                                                t('memberEditDialog.bankingDetails.fields.iban.placeholder') :
                                                t('memberEditDialog.bankingDetails.fields.iban.obfuscated')}
                                            aria-label={t('memberEditDialog.bankingDetails.fields.iban.ariaLabel')}
                                            value={isIbanEditable ? formatIBAN(field.value || '') : field.value || ''}
                                            onChange={(e) => {
                                                const unformatted = unformatIBAN(e.target.value);
                                                field.onChange(unformatted);
                                            }}
                                        />
                                    )}
                                />
                                {!isIbanEditable && isDefined(ibanValue) && (
                                    <Button
                                        type="button"
                                        plain
                                        onClick={handleEditIban}
                                        aria-label={t('memberEditDialog.bankingDetails.buttons.editIban.ariaLabel')}
                                    >
                                        {t('memberEditDialog.bankingDetails.buttons.editIban.text')}
                                    </Button>
                                )}
                            </div>
                        </Field>
                        {errors.iban && (
                            <ValidationAlert
                                message={t(errors.iban?.message ||
                                    "memberEditDialog.bankingDetails.errors.unknown-iban-issue")}
                            />
                        )}
                    </div>


                    <div>
                        <Field>
                            <Label>{t('memberEditDialog.bankingDetails.fields.bic.label')}</Label>
                            <Input
                                {...register("bic")}
                                type="text"
                                autoComplete="bic"
                                aria-label={t('memberEditDialog.bankingDetails.fields.bic.ariaLabel')}
                                invalid={Boolean(errors.bic)}
                                placeholder={t('memberEditDialog.bankingDetails.fields.bic.placeholder')}
                            />

                            {errors.bic && (
                                <ValidationAlert message={t(errors.bic?.message ||
                                    "memberEditDialog.bankingDetails.errors.unknown-title-issues")}/>
                            )}
                        </Field>
                    </div>

                    <div>
                        <Field>
                            <Label>
                                {t('memberEditDialog.bankingDetails.fields.bankName.label')}
                            </Label>
                            <Input
                                {...register("bankName")}
                                type="text"
                                invalid={Boolean(errors.bankName)}
                                aria-label={t('memberEditDialog.bankingDetails.fields.bankName.ariaLabel')}
                                placeholder={t('memberEditDialog.bankingDetails.fields.bankName.placeholder')}
                            />
                            {errors.bankName && (
                                <ValidationAlert
                                    message={t(errors.bankName?.message ||
                                        "memberEditDialog.bankingDetails.errors.unknown-bankName-issues")}
                                />
                            )}
                        </Field>
                    </div>

                    <div>
                        <Field>
                            <Label>
                                {t('memberEditDialog.bankingDetails.fields.mandateReference.label')}
                            </Label>
                            <Input
                                type="text"
                                {...register('mandateReference')}
                                invalid={Boolean(errors.mandateReference)}
                                aria-label={t('memberEditDialog.bankingDetails.fields.mandateReference.ariaLabel')}
                                placeholder={t('memberEditDialog.bankingDetails.fields.mandateReference.placeholder')}
                            />
                            {errors.mandateReference && (
                                <ValidationAlert
                                    message={t(errors.mandateReference?.message ||
                                        "memberEditDialog.bankingDetails.errors.unknown-mandateReference-issue")}
                                />
                            )}
                        </Field>
                    </div>

                    <div>
                        <Field>
                            <Label>
                                {t('memberEditDialog.bankingDetails.fields.mandateDate.label')}
                            </Label>

                            <Controller
                                name="mandateDate"
                                control={control}
                                render={({field}) => (
                                    <Input
                                        type="date"
                                        value={field.value || ''}
                                        onChange={(e) => {
                                            const value =
                                                e.target.value || null;
                                            field.onChange(value);
                                        }}
                                        aria-label={t('memberEditDialog.bankingDetails.fields.mandateDate.label')}
                                        invalid={Boolean(errors.mandateDate)}
                                    />
                                )}
                            />

                            {errors.mandateDate && (
                                <ValidationAlert
                                    message={t(errors.mandateDate?.message ||
                                        "memberEditDialog.bankingDetails.errors.unknown-mandateDate-issue")}
                                />
                            )}
                        </Field>
                    </div>

                    <DialogActions className="flex flex-col sm:flex-row w-full gap-2">
                        {Boolean(memberData.bankingDetails?.id) && (
                            <>
                                <div className="order-2 sm:order-1 sm:flex-grow text-left">
                                    <Button
                                        type="button"
                                        color={"red"}
                                        onClick={openDeleteConfirmation}
                                        aria-label={t('memberEditDialog.bankingDetails.buttons.delete.ariaLabel')}
                                        className="w-full sm:w-auto"
                                    >
                                        {t('memberEditDialog.bankingDetails.buttons.delete.text')}
                                    </Button>
                                </div>

                                {confirmationDialog.dialogProps && (
                                    <ConfirmationDialog
                                        {...confirmationDialog.dialogProps}
                                        onConfirm={onDeleteDetails}
                                        namespace="common"
                                    />
                                )}
                            </>
                        )}

                        <div className="order-1 sm:order-2 flex flex-col sm:flex-row w-full sm:w-auto gap-2">
                            <Button
                                type="button"
                                plain
                                onClick={handleClosing}
                                aria-label={tCommon('buttons.cancel.ariaLabel')}
                                className="w-full sm:w-auto"
                            >
                                {tCommon('buttons.cancel.text')}
                            </Button>
                            <Button
                                type="submit"
                                aria-label={tCommon('buttons.submit.ariaLabel')}
                                className="w-full sm:w-auto"
                            >
                                {tCommon('buttons.submit.text')}
                            </Button>
                        </div>
                    </DialogActions>
                </form>
            </DialogBody>
        </Dialog>
    );
}
